"use client";
import { GOOGLE_MAPS_API_KEY } from '@/utils/constants/apiKeys';
import { createLogger } from '@/utils/logger/devLogger';

const googleMapsLogger = createLogger('GoogleMaps');

// A global flag to track if we're currently loading the script
let isLoadingScript = false;

// A global flag to check if the API is already loaded
let isGoogleMapsLoaded = false;

// Track deprecation warnings to help with migration planning
let deprecationWarningsDetected: string[] = [];

/**
 * Loads the Google Maps API with the Places library.
 * This is designed to be called from multiple components while ensuring the script is only loaded once.
 * 
 * @returns {Promise<void>} A promise that resolves when the API is loaded
 */
export const loadGoogleMapsApi = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If Google Maps is already loaded, resolve immediately
    // Check for PlaceAutocompleteElement specifically for new API support
    if (typeof window !== 'undefined' && 
        window.google?.maps?.places && 
        window.google?.maps?.places?.PlaceAutocompleteElement) {
      isGoogleMapsLoaded = true;
      resolve();
      return;
    }

    // If the script is currently being loaded by another component, wait for it
    if (isLoadingScript) {
      const checkIfLoaded = () => {
        if (typeof window !== 'undefined' && 
            window.google?.maps?.places &&
            window.google?.maps?.places?.PlaceAutocompleteElement) {
          isGoogleMapsLoaded = true;
          resolve();
        } else {
          setTimeout(checkIfLoaded, 100); // Check again in 100ms
        }
      };
      checkIfLoaded();
      return;
    }

    // Load the script if not already loading or loaded
    const apiKey = GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      reject(new Error('Google Maps API key is missing. Please set GOOGLE_MAPS_API_KEY in your environment.'));
      return;
    }
    
    isLoadingScript = true;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait for PlaceAutocompleteElement to be available after script loads
      const waitForPlaceAutocomplete = () => {
        if (window.google?.maps?.places?.PlaceAutocompleteElement) {
          isLoadingScript = false;
          isGoogleMapsLoaded = true;
          resolve();
        } else {
          // Check again in 50ms (PlaceAutocompleteElement loads async)
          setTimeout(waitForPlaceAutocomplete, 50);
        }
      };
      
      waitForPlaceAutocomplete();
    };
    
    script.onerror = () => {
      isLoadingScript = false;
      reject(new Error('Failed to load Google Maps API script'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Check if Google Maps API is loaded
 * 
 * @returns {boolean} True if Google Maps API is loaded
 */
export const isGoogleMapsApiLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window.google?.maps?.places;
};

/**
 * Monitor console for Google Maps deprecation warnings
 * This helps us proactively identify when Google announces API changes
 */
export const monitorGoogleMapsWarnings = (): void => {
  if (typeof window === 'undefined') return;

  // Override console.warn to capture Google Maps related warnings
  const originalWarn = console.warn;
  console.warn = (...args: any[]) => {
    const message = args.join(' ').toLowerCase();
    
    // Check for Google Maps related warnings
    if (message.includes('google') && message.includes('maps') && 
        (message.includes('deprecated') || message.includes('deprecation') || 
         message.includes('legacy') || message.includes('migration'))) {
      
      const warningText = args.join(' ');
      if (!deprecationWarningsDetected.includes(warningText)) {
        deprecationWarningsDetected.push(warningText);
        googleMapsLogger.warn('🚨 Google Maps Deprecation Warning Detected:', warningText);
        googleMapsLogger.info('📋 Add this to migration plan documentation for review');
      }
    }
    
    // Call original console.warn
    originalWarn.apply(console, args);
  };

  googleMapsLogger.info('✅ Google Maps deprecation warning monitoring enabled');
};

/**
 * Enhanced Google Maps API loading with monitoring
 */
export const loadGoogleMapsApiWithMonitoring = (): Promise<void> => {
  monitorGoogleMapsWarnings();
  
  return loadGoogleMapsApi().then(() => {
    googleMapsLogger.info('✅ Google Maps API loaded successfully');
  }).catch((error) => {
    googleMapsLogger.error('❌ Failed to load Google Maps API:', error);
    throw error;
  });
};

// =============================================================================
// PLACES API (NEW) SUPPORT - Phase 2 Migration
// =============================================================================

/**
 * Configuration options for Places autocomplete widgets
 */
export interface PlacesAutocompleteOptions {
  // Core options actually used in the app
  fields?: string[];
  componentRestrictions?: { country: string | string[] };
  types?: string[];
}

/**
 * Checks if Places API (New) widgets are supported
 */
export const isPlacesApiNewSupported = (): boolean => {
  return typeof window !== 'undefined' && 
         !!window.google?.maps?.places?.PlaceAutocompleteElement;
};

/**
 * Setup Google Places API (New) autocomplete widget
 * Requires modern browser with Places API (New) support
 * Uses the same DOM integration pattern as AddressCard.tsx
 */
export const setupNewPlacesAutocomplete = (
  inputElement: HTMLInputElement,
  options: PlacesAutocompleteOptions = {}
) => {
  if (!isPlacesApiNewSupported()) {
    throw new Error('Google Places API (New) is not supported in this environment');
  }
  
  if (!inputElement || !inputElement.parentNode) {
    throw new Error('Valid input element with parent node is required');
  }
  
  // Use the exact same approach as AddressCard.tsx - direct constructor
  const constructorOptions: any = {
    componentRestrictions: options.componentRestrictions || { country: 'us' }
  };
  
  //@ts-ignore - PlaceAutocompleteElement is in preview
  const autocompleteInstance = new window.google.maps.places.PlaceAutocompleteElement(constructorOptions);
  
  // Use the exact same DOM integration pattern as AddressCard.tsx
  inputElement.style.display = 'none';  // Hide original input
  inputElement.parentNode.appendChild(autocompleteInstance as unknown as Node);  // Append to parent
  
  googleMapsLogger.info('✅ Google Places API (New) widget created successfully');
  return { type: 'new', widget: autocompleteInstance };
};

/**
 * Setup Google Places API (New) autocomplete widget directly in container
 * For cases where we don't need an input element to replace
 */
export const setupPlacesWidgetInContainer = (
  containerElement: HTMLElement,
  options: PlacesAutocompleteOptions = {}
) => {
  if (!isPlacesApiNewSupported()) {
    throw new Error('Google Places API (New) is not supported in this environment');
  }
  
  if (!containerElement) {
    throw new Error('Valid container element is required');
  }
  
  // Create constructor options
  const constructorOptions: any = {
    componentRestrictions: options.componentRestrictions || { country: 'us' }
  };
  
  //@ts-ignore - PlaceAutocompleteElement is in preview
  const autocompleteInstance = new window.google.maps.places.PlaceAutocompleteElement(constructorOptions);
  
  // Clear container and append widget
  containerElement.innerHTML = '';
  containerElement.appendChild(autocompleteInstance as unknown as Node);
  
  googleMapsLogger.info('✅ Google Places widget inserted into container successfully');
  return autocompleteInstance;
};

/**
 * Event handler for Google Places API (New) autocomplete widget
 */
export const setupNewPlacesEventHandler = (
  widget: google.maps.places.PlaceAutocompleteElement,
  onPlaceSelected: (place: any) => void
) => {
  // Places API (New) event handling
  //@ts-ignore
  widget.addEventListener('gmp-select', async ({ placePrediction }) => {
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({ 
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] 
      });
      
      googleMapsLogger.debug('📍 Places API (New) place selected:', place.toJSON());
      onPlaceSelected(place);
    } catch (error) {
      googleMapsLogger.error('❌ Error handling Places API (New) selection:', error);
    }
  });
}; 